import styled from "styled-components";
import { TubeBar } from "./FGChartTube";
import { CircularGauge } from "./CircularGauge";
import { useIN12aDispatch } from "../../../hooks/BWOS/useIN12aDispatch";
import { useOrientation } from "../../../hooks/useOrienttation";

const ChartWrapper = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: column;
  width: 100%;
`;

const StyledGroupContainer = styled.div`
  display: flex;
  gap: ${(o) => (o.$isPortrait ? "4.6rem" : "1.6rem")};
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
`;
const StyledGroup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center; /* Center content horizontally */
`;

const GroupedBarStyle = styled.div`
  display: flex;
  gap: ${(o) => (o.$isPortrait ? "1.2rem" : "0.9rem")};
  justify-content: center;
  flex-wrap: wrap;
`;

const StyledGroupHeading = styled.h5`
  margin: 0;
  padding-bottom: 0.5rem;
  color: var(--color-grey-600);
  font-weight: 600;
  font-size: 0.95rem;
  text-align: center;
`;

const GlobalGaugesWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 2rem;
  margin-top: -1rem;
`;

function Dispatch({ height }) {
  const { data, isLoading, error } = useIN12aDispatch();
  const orientation = useOrientation();
  const isPortrait = orientation === "portrait";

  if (isLoading) return <div>Loading...</div>;
  if (error) return <div>Error loading data</div>;

  const maxTotalSpace = Math.max(...data.map((c) => c.spaceTotal));

  const getFillGrade = (occupied, total) =>
    total > 0 ? (occupied / total) * 100 : 0;

  let totalOccupied = 0,
    totalSpace = 0,
    totalOccupied1500 = 0,
    totalSpace1500 = 0,
    totalOccupied1800 = 0,
    totalSpace1800 = 0,
    totalOccupied2150 = 0,
    totalSpace2150 = 0,
    totalOccupied2400 = 0,
    totalSpace2400 = 0;

  const enrichedData = data?.map((crane) => {
    totalOccupied += crane.occupiedTotal;
    totalSpace += crane.spaceTotal;
    totalOccupied1500 += crane.occupied1500;
    totalSpace1500 += crane.space1500;
    totalOccupied1800 += crane.occupied1800;
    totalSpace1800 += crane.space1800;
    totalOccupied2150 += crane.occupied2150;
    totalSpace2150 += crane.space2150;
    totalOccupied2400 += crane.occupied2400;
    totalSpace2400 += crane.space2400;

    const fillTotal = getFillGrade(crane.occupiedTotal, crane.spaceTotal);

    return {
      ...crane,
      fillTotal,
      fill200: getFillGrade(crane.occupied200, crane.space200),
      fill290: getFillGrade(crane.occupied290, crane.space290),
      fill425: getFillGrade(crane.occupied425, crane.space425),
    };
  });

  // const globalFill = {
  //   total: getFillGrade(totalOccupied, totalSpace).toFixed(1),
  //   h1050: getFillGrade(totalOccupied1050, totalSpace1050).toFixed(1),
  //   h1250: getFillGrade(totalOccupied1250, totalSpace1250).toFixed(1),
  //   h1500: getFillGrade(totalOccupied1500, totalSpace1500).toFixed(1),
  //   h2150: getFillGrade(totalOccupied2150, totalSpace2150).toFixed(1),
  //   h2400: getFillGrade(totalOccupied2400, totalSpace2400).toFixed(1),
  // };

  const fillGradeThresholds = {
    1500: { yellow: 75, red: 90 },
    1800: { yellow: 75, red: 90 },
    2150: { yellow: 75, red: 90 },
    2400: { yellow: 75, red: 90 },
  };

  function getFillColor(label, occupied, total, color) {
    if (!total || !occupied) return "#000000";
    const percent = (occupied / total) * 100;
    const limits = fillGradeThresholds[label];
    if (!limits) return "#5a4343";

    if (percent >= limits.red) return "#e63946"; // red
    if (percent >= limits.yellow) return "#f1c40f"; // yellow
    return color; // green
  }

  function CraneGroup({
    crane,
    value1,
    max1,
    value2,
    max2,
    value3,
    max3,
    value4,
    max4,
    height,
  }) {
    return (
      <GroupedBarStyle $isPortrait={isPortrait}>
        <TubeBar
          key={`low-${crane}`}
          yValue={value1}
          refValue={null}
          maxYValue={max1}
          label="1050"
          machine={crane}
          color={getFillColor("1500", value1, max1, "var(--crane-height-low)")}
          height={height}
        />
        <TubeBar
          key={`medium-${crane}`}
          yValue={value2}
          refValue={null}
          maxYValue={max2}
          label="1250"
          machine={crane}
          color={getFillColor("1800", value2, max2, "var(--crane-height-md)")}
          height={height}
        />
        <TubeBar
          key={`high-${crane}`}
          yValue={value3}
          refValue={null}
          maxYValue={max3}
          label="1500"
          machine={crane}
          color={getFillColor("2150", value3, max3, "var(--crane-height-hi)")}
          height={height}
        />
        <TubeBar
          key={`top-${crane}`}
          yValue={value4}
          refValue={null}
          maxYValue={max4}
          label="2150"
          machine={crane}
          color={getFillColor("2400", value4, max4, "var(--crane-height-top)")}
          height={height}
        />
      </GroupedBarStyle>
    );
  }

  return (
    <ChartWrapper>
      <StyledGroupContainer $isPortrait={isPortrait}>
        {data.map((crane) => (
          <StyledGroup key={crane.crane}>
            <StyledGroupHeading>Crane {crane.crane}</StyledGroupHeading>
            <CraneGroup
              key={crane.crane}
              crane={crane.crane}
              value1={crane.occupied1500}
              max1={crane.space1500}
              value2={crane.occupied1800}
              max2={crane.space1800}
              value3={crane.occupied2150}
              max3={crane.space2150}
              value4={crane.occupied2400}
              max4={crane.space2400}
              height={height} // optional custom height for this row
            />
          </StyledGroup>
        ))}
      </StyledGroupContainer>

      {/* <GlobalGaugesWrapper>
        <CircularGauge value={+globalFill.total} label="Total" />
        <CircularGauge value={+globalFill.h200} label="200" />
        <CircularGauge value={+globalFill.h290} label="290" />
        <CircularGauge value={+globalFill.h425} label="425" />
      </GlobalGaugesWrapper> */}
    </ChartWrapper>
  );
}

export default Dispatch;
